// The canvas's colours, read from CSS custom properties at runtime.
//
// Every colour the renderer paints is a `--soc-*` token declared in
// src/styles/app.css. Nothing here or in the renderer carries a literal hex:
// the design-tokens check would fail it, and more importantly the theme stays
// in one file. Values are cached because getComputedStyle on every frame is
// not free; `refreshPalette()` is called when settings change.

const KEYS = [
  'sky-top', 'sky-mid', 'sky-horizon', 'sky-glow', 'ground', 'road',
  'kerb', 'verge', 'fence', 'lamp', 'lamp-glow', 'filler', 'filler-roof',
  'hill-far', 'hill-near', 'tree', 'tree-lit',
  'cable', 'cable-active', 'white',
  'bulb-off', 'bulb-1', 'bulb-2', 'bulb-3', 'bulb-4', 'bulb-5',
  'spark', 'spark-glow',
  'house-dark', 'house-lit', 'roof-dark', 'roof-lit', 'verandah',
  'window-dark', 'window-glow', 'door',
  'gold', 'tangle', 'star', 'text', 'junction', 'junction-ring',
  'bauble', 'bauble-cap', 'bell', 'cockatoo', 'cockatoo-crest', 'solar',
  'particle', 'meter', 'ribbon', 'stars',
  'map-land', 'map-coast', 'town', 'town-off',
];

/** Kebab-case token → camelCase key: `sky-top` → `skyTop`, `bulb-1` → `bulb1`. */
const camel = (k) => k.replace(/-([a-z0-9])/g, (_, c) => c.toUpperCase());

let cache = null;

function readAll() {
  const out = {};
  if (typeof document === 'undefined') return out;
  const style = getComputedStyle(document.documentElement);
  for (const key of KEYS) {
    const v = style.getPropertyValue(`--soc-${key}`).trim();
    out[camel(key)] = v || 'currentColor';
  }
  out.bulbs = [out.bulb1, out.bulb2, out.bulb3, out.bulb4, out.bulb5];
  return out;
}

/** The palette, cached. */
export function palette() {
  if (!cache) cache = readAll();
  return cache;
}

/** Re-read after a settings change or a theme swap. */
export function refreshPalette() {
  cache = readAll();
  return cache;
}

/**
 * A colour with alpha. Tokens are hex in app.css, but this accepts rgb() too,
 * because the renderer must never assume the format the stylesheet chose.
 */
const alphaCache = new Map();
export function withAlpha(color, alpha) {
  const key = `${color}|${alpha}`;
  const hit = alphaCache.get(key);
  if (hit) return hit;
  let out = color;
  const c = String(color).trim();
  if (c[0] === '#') {
    let r; let g; let b;
    if (c.length === 4 || c.length === 5) {
      r = parseInt(c[1] + c[1], 16); g = parseInt(c[2] + c[2], 16); b = parseInt(c[3] + c[3], 16);
    } else {
      r = parseInt(c.slice(1, 3), 16); g = parseInt(c.slice(3, 5), 16); b = parseInt(c.slice(5, 7), 16);
    }
    out = `rgba(${r}, ${g}, ${b}, ${alpha})`;
  } else {
    const m = c.match(/rgba?\(([^)]+)\)/);
    if (m) {
      const parts = m[1].split(/[\s,/]+/).filter(Boolean).slice(0, 3);
      out = `rgba(${parts.join(', ')}, ${alpha})`;
    }
  }
  if (alphaCache.size > 500) alphaCache.clear();
  alphaCache.set(key, out);
  return out;
}

/** Linear mix of two hex colours, 0 → a, 1 → b. Returns rgb(). */
const mixCache = new Map();
export function mix(a, b, t) {
  const tt = Math.round(Math.max(0, Math.min(1, t)) * 20) / 20;
  const key = `${a}|${b}|${tt}`;
  const hit = mixCache.get(key);
  if (hit) return hit;
  const pa = parseHex(a);
  const pb = parseHex(b);
  if (!pa || !pb) return tt < 0.5 ? a : b;
  const r = Math.round(pa[0] + (pb[0] - pa[0]) * tt);
  const g = Math.round(pa[1] + (pb[1] - pa[1]) * tt);
  const bl = Math.round(pa[2] + (pb[2] - pa[2]) * tt);
  const out = `rgb(${r}, ${g}, ${bl})`;
  if (mixCache.size > 500) mixCache.clear();
  mixCache.set(key, out);
  return out;
}

function parseHex(c) {
  const s = String(c).trim();
  if (s[0] !== '#') {
    const m = s.match(/rgba?\(([^)]+)\)/);
    if (!m) return null;
    return m[1].split(/[\s,/]+/).filter(Boolean).slice(0, 3).map(Number);
  }
  if (s.length === 4) return [parseInt(s[1] + s[1], 16), parseInt(s[2] + s[2], 16), parseInt(s[3] + s[3], 16)];
  return [parseInt(s.slice(1, 3), 16), parseInt(s.slice(3, 5), 16), parseInt(s.slice(5, 7), 16)];
}
